/**
 * Catálogo das preferências do sistema — porte de `ui_kits/vecchia_app/configData.jsx`.
 *
 * `padrao` é DADO, não texto: é ele que gera o "(Padrão: …)", o marcador de
 * "alterado" e a ação de restaurar. O banco só guarda o que a clínica mudou
 * (`preferencias_da_clinica`); restaurar apaga a linha.
 *
 * **`efeito` diz onde a preferência muda alguma coisa.** A maioria ainda não
 * muda nada — o protótipo desenhou as 75 de uma vez, e cada uma depende de um
 * módulo que a respeite. Preferência sem `efeito` aparece marcada "ainda sem
 * efeito": guardar a escolha é real, fingir que ela já age não seria.
 */

import type { ValorDePreferencia } from './cadastros';

export type SecaoDePreferencia =
    | 'geral' | 'financeiro' | 'agenda' | 'atendimentos' | 'contatos' | 'impressao'
    | 'comissoes' | 'orcamentos' | 'estoque' | 'seguranca' | 'vendas' | 'notificacoes';

export const SECOES_DE_PREFERENCIA: { id: SecaoDePreferencia; rotulo: string }[] = [
    { id: 'geral', rotulo: 'Geral' },
    { id: 'financeiro', rotulo: 'Financeiro' },
    { id: 'agenda', rotulo: 'Agenda' },
    { id: 'atendimentos', rotulo: 'Atendimentos' },
    { id: 'contatos', rotulo: 'Contatos' },
    { id: 'impressao', rotulo: 'Impressão' },
    { id: 'comissoes', rotulo: 'Comissões' },
    { id: 'orcamentos', rotulo: 'Orçamentos' },
    { id: 'estoque', rotulo: 'Estoque' },
    { id: 'seguranca', rotulo: 'Segurança' },
    { id: 'vendas', rotulo: 'Vendas' },
    { id: 'notificacoes', rotulo: 'Notificações' },
];

export type TipoDePreferencia = 'boolean' | 'select' | 'number' | 'text' | 'composta';

/** O formulário que a preferência composta abre. */
export type EditorComposto = 'lembrete' | 'campos' | 'comissao' | 'impressao' | 'texto';

/**
 * De onde vêm as opções quando elas são cadastro da clínica, e não lista fixa.
 *
 * O protótipo trazia contas e categorias inventadas ("Banco padrão",
 * "Recebíveis"). Numa clínica de verdade a opção precisa ser uma conta que
 * existe — senão a preferência aponta para nada.
 */
export type FonteDeOpcoes = 'contas' | 'metodos' | 'categorias-receita' | 'categorias-despesa';

export interface OpcaoDePreferencia {
    valor: string;
    rotulo: string;
}

export interface Preferencia {
    chave: string;
    secao: SecaoDePreferencia;
    tipo: TipoDePreferencia;
    rotulo: string;
    /** `null` nas de opção dinâmica: não há conta "padrão" antes de a clínica escolher. */
    padrao: ValorDePreferencia | null;
    /** Como o padrão aparece escrito, nas compostas. */
    padraoRotulo?: string;
    descricao?: string;
    opcoes?: OpcaoDePreferencia[];
    fonte?: FonteDeOpcoes;
    unidade?: string;
    editor?: EditorComposto;
    /** Onde a preferência já muda algo. Ausente: ainda sem efeito. */
    efeito?: string;
    /**
     * Só a proprietária muda. A guarda é do banco (0156); aqui o campo só
     * aparece travado, para ninguém descobrir a regra pelo erro ao salvar.
     */
    soProprietaria?: boolean;
}

const op = (...pares: [string, string?][]): OpcaoDePreferencia[] =>
    pares.map(([valor, rotulo]) => ({ valor, rotulo: rotulo ?? valor }));

const METODOS = op(['dinheiro', 'Dinheiro'], ['pix', 'Pix'], ['cartao-credito', 'Cartão de crédito'], ['cartao-debito', 'Cartão de débito'], ['boleto', 'Boleto']);

export const PREFERENCIAS: Preferencia[] = [
    /* ---------- Geral ---------- */
    {
        chave: 'geral.fuso', secao: 'geral', tipo: 'select', rotulo: 'Fuso horário', padrao: 'sp',
        descricao: 'Base de data e hora de agendamentos, títulos e relatórios.',
        opcoes: op(['sp', '(GMT-03:00) São Paulo'], ['manaus', '(GMT-04:00) Manaus'], ['rio-branco', '(GMT-05:00) Rio Branco'], ['noronha', '(GMT-02:00) Fernando de Noronha']),
    },
    {
        chave: 'geral.moeda', secao: 'geral', tipo: 'select', rotulo: 'Moeda', padrao: 'brl',
        descricao: 'Formatação de valores em todo o sistema.',
        opcoes: op(['brl', 'BRL - R$'], ['usd', 'USD - US$'], ['eur', 'EUR - €']),
    },

    /* ---------- Financeiro ---------- */
    { chave: 'fin.ocultar', secao: 'financeiro', tipo: 'boolean', rotulo: 'Ocultar dados financeiros', padrao: false, descricao: 'Esconde informações financeiras da página inicial.' },
    { chave: 'fin.dre', secao: 'financeiro', tipo: 'boolean', rotulo: 'Usar DRE', padrao: false, descricao: 'Habilita as categorias do DRE nos lançamentos.' },
    { chave: 'fin.caixa', secao: 'financeiro', tipo: 'boolean', rotulo: 'Usar abertura de caixa', padrao: false, descricao: 'Exige abertura e fechamento de caixa por turno.' },
    {
        chave: 'fin.periodicidade-caixa', secao: 'financeiro', tipo: 'select', rotulo: 'Periodicidade de fechamento de caixa', padrao: 'diario',
        descricao: 'Frequência esperada para alertas de caixa pendente.',
        opcoes: op(['diario', 'Diário'], ['semanal', 'Semanal'], ['mensal', 'Mensal']),
    },
    { chave: 'fin.so-dinheiro', secao: 'financeiro', tipo: 'boolean', rotulo: 'Mostrar apenas movimentações de Dinheiro no caixa', padrao: false },
    {
        chave: 'fin.conciliacao', secao: 'financeiro', tipo: 'boolean', rotulo: 'Conciliação bancária', padrao: false, descricao: 'Importa extrato e sugere baixa automática de títulos.',
        efeito: 'Libera a importação de OFX em Financeiro › Contas.',
    },
    { chave: 'fin.cat-receitas', secao: 'financeiro', tipo: 'select', rotulo: 'Categoria de receitas', padrao: null, fonte: 'categorias-receita', efeito: 'Vem escolhida no novo lançamento a receber.' },
    { chave: 'fin.met-receitas', secao: 'financeiro', tipo: 'select', rotulo: 'Método de receitas', padrao: null, fonte: 'metodos' },
    { chave: 'fin.met-despesas', secao: 'financeiro', tipo: 'select', rotulo: 'Método de despesas', padrao: null, fonte: 'metodos' },
    { chave: 'fin.conta-receitas', secao: 'financeiro', tipo: 'select', rotulo: 'Conta de receitas', padrao: null, fonte: 'contas' },
    { chave: 'fin.conta-despesas', secao: 'financeiro', tipo: 'select', rotulo: 'Conta de despesas', padrao: null, fonte: 'contas' },
    { chave: 'fin.cat-transferencias', secao: 'financeiro', tipo: 'select', rotulo: 'Categoria de transferências', padrao: null, fonte: 'categorias-despesa' },
    { chave: 'fin.cat-taxas', secao: 'financeiro', tipo: 'select', rotulo: 'Categoria de taxas do gateway', padrao: null, fonte: 'categorias-despesa' },
    { chave: 'fin.cat-desc-receber', secao: 'financeiro', tipo: 'select', rotulo: 'Categoria de descontos em contas a receber', padrao: null, fonte: 'categorias-despesa' },
    { chave: 'fin.cat-desc-pagar', secao: 'financeiro', tipo: 'select', rotulo: 'Categoria de descontos em contas a pagar', padrao: null, fonte: 'categorias-receita' },
    { chave: 'fin.cat-juros-pagar', secao: 'financeiro', tipo: 'select', rotulo: 'Categoria de juros e multas em contas a pagar', padrao: null, fonte: 'categorias-despesa' },
    { chave: 'fin.cat-juros-receber', secao: 'financeiro', tipo: 'select', rotulo: 'Categoria de juros e multas em contas a receber', padrao: null, fonte: 'categorias-receita' },
    { chave: 'fin.cat-comissoes', secao: 'financeiro', tipo: 'select', rotulo: 'Categoria de comissões', padrao: null, fonte: 'categorias-despesa' },
    { chave: 'fin.cat-sangria', secao: 'financeiro', tipo: 'select', rotulo: 'Categoria de sangria', padrao: null, fonte: 'categorias-despesa' },
    { chave: 'fin.cat-suprimento', secao: 'financeiro', tipo: 'select', rotulo: 'Categoria de suprimento', padrao: null, fonte: 'categorias-receita' },
    { chave: 'fin.taxa-paciente', secao: 'financeiro', tipo: 'boolean', rotulo: 'Transferir taxa para paciente', padrao: false, descricao: 'A taxa do gateway é somada ao valor cobrado.' },

    /* ---------- Agenda ---------- */
    { chave: 'ag.concluir-auto', secao: 'agenda', tipo: 'boolean', rotulo: 'Concluir agendamentos automaticamente', padrao: true, descricao: 'Agendamentos passados sem atendimento viram concluídos.' },
    { chave: 'ag.so-profissionais', secao: 'agenda', tipo: 'boolean', rotulo: 'Agendamentos apenas para Profissionais', padrao: false },
    { chave: 'ag.comanda-auto', secao: 'agenda', tipo: 'boolean', rotulo: 'Criar comanda automaticamente', padrao: false },
    { chave: 'ag.convenios', secao: 'agenda', tipo: 'boolean', rotulo: 'Habilitar convênios', padrao: false },
    { chave: 'ag.lista-espera', secao: 'agenda', tipo: 'boolean', rotulo: 'Habilitar lista de espera', padrao: false },
    { chave: 'ag.sala-espera', secao: 'agenda', tipo: 'boolean', rotulo: 'Habilitar sala de espera', padrao: false, descricao: 'Ao trocar o status para "aguardando", o paciente entra na sala de espera.' },
    { chave: 'ag.nao-revisado', secao: 'agenda', tipo: 'boolean', rotulo: 'Habilitar status de agendamentos não revisados', padrao: false, descricao: 'Agendamentos de integrações externas nascem como "Não revisado".' },
    { chave: 'ag.so-funcionamento', secao: 'agenda', tipo: 'boolean', rotulo: 'Mostrar apenas horários de funcionamento', padrao: false, descricao: 'A grade passa a respeitar os horários de funcionamento da clínica.' },
    { chave: 'ag.regras-disponibilidade', secao: 'agenda', tipo: 'boolean', rotulo: 'Usar regras de disponibilidade', padrao: false },
    { chave: 'ag.so-profissional-inicia', secao: 'agenda', tipo: 'boolean', rotulo: 'Apenas o profissional inicia o agendamento', padrao: false },
    { chave: 'ag.feriados', secao: 'agenda', tipo: 'boolean', rotulo: 'Bloquear horários em feriados nacionais', padrao: false },
    {
        chave: 'ag.intervalo', secao: 'agenda', tipo: 'select', rotulo: 'Intervalo de tempo da agenda', padrao: '30',
        descricao: 'Altura de cada faixa na grade.',
        opcoes: op(['10', '10 minutos'], ['15', '15 minutos'], ['20', '20 minutos'], ['30', '30 minutos'], ['60', '60 minutos']),
    },
    {
        chave: 'ag.cor', secao: 'agenda', tipo: 'select', rotulo: 'Cor do agendamento', padrao: 'profissional',
        opcoes: op(['profissional', 'Profissional'], ['procedimento', 'Procedimento'], ['status', 'Status'], ['sala', 'Sala']),
    },
    {
        chave: 'ag.primeiro-dia', secao: 'agenda', tipo: 'select', rotulo: 'Primeiro dia da semana', padrao: 'domingo',
        opcoes: op(['domingo', 'Domingo'], ['segunda', 'Segunda-feira']),
    },
    /* O lembrete de agendamento JÁ existe: é a régua da aba Notificações. A
       preferência leva até lá em vez de abrir um segundo formulário para a
       mesma coisa — dois lugares para o mesmo lembrete divergiriam. */
    {
        chave: 'ag.lembrete-agendamento', secao: 'agenda', tipo: 'composta', rotulo: 'Lembrete de agendamento', padrao: null,
        padraoRotulo: 'Régua de Notificações', editor: 'lembrete',
        descricao: 'Canal, antecedência e texto da mensagem.',
        efeito: 'É a régua de lembretes da aba Notificações.',
    },
    { chave: 'ag.lembrete-bloqueio', secao: 'agenda', tipo: 'composta', rotulo: 'Lembrete de bloqueio de horário', padrao: null, padraoRotulo: 'Desabilitado', editor: 'lembrete' },
    { chave: 'ag.lembrete-lembrete', secao: 'agenda', tipo: 'composta', rotulo: 'Lembrete de lembrete', padrao: null, padraoRotulo: 'Desabilitado', editor: 'lembrete' },
    { chave: 'ag.aviso-criado', secao: 'agenda', tipo: 'composta', rotulo: 'Aviso de agendamento criado', padrao: null, padraoRotulo: 'Habilitado', editor: 'lembrete' },
    { chave: 'ag.aviso-alterado', secao: 'agenda', tipo: 'composta', rotulo: 'Aviso de agendamento alterado', padrao: null, padraoRotulo: 'Habilitado', editor: 'lembrete' },
    { chave: 'ag.aviso-cancelado', secao: 'agenda', tipo: 'composta', rotulo: 'Aviso de agendamento cancelado', padrao: null, padraoRotulo: 'Habilitado', editor: 'lembrete' },
    { chave: 'ag.aviso-confirmado', secao: 'agenda', tipo: 'composta', rotulo: 'Aviso de agendamento confirmado', padrao: null, padraoRotulo: 'Desabilitado', editor: 'lembrete' },
    { chave: 'ag.reserva-temporaria', secao: 'agenda', tipo: 'boolean', rotulo: 'Habilitar reserva temporária de horários', padrao: true, descricao: 'Reserva o horário nos fluxos conversacionais da IA enquanto o paciente decide.' },
    { chave: 'ag.tempo-reserva', secao: 'agenda', tipo: 'number', rotulo: 'Tempo máximo de reserva de horário', padrao: 30, unidade: 'minutos' },

    /* ---------- Atendimentos ---------- */
    { chave: 'at.rascunhos', secao: 'atendimentos', tipo: 'boolean', rotulo: 'Permitir criar atendimentos rascunhos', padrao: true, descricao: 'Salva automaticamente atendimentos não finalizados.', efeito: 'Guarda o rascunho da ficha enquanto a consulta está aberta.' },
    {
        chave: 'at.resumo-ia', secao: 'atendimentos', tipo: 'select', rotulo: 'Quem vê o resumo da consulta feito pela IA', padrao: 'profissional',
        opcoes: op(['profissional', 'Profissionais e proprietária'], ['todos', 'Recepção, profissionais e proprietária'], ['proprietaria', 'Só a proprietária']),
        descricao: 'O resumo aprovado aparece ao abrir a consulta na linha do tempo do prontuário. Quem não pode ver sabe que ele existe, sem ler o texto.',
        efeito: 'Decide quem lê o resumo da IA no prontuário do paciente.',
        soProprietaria: true,
    },
    { chave: 'at.cid', secao: 'atendimentos', tipo: 'boolean', rotulo: 'Habilitar CID', padrao: false },
    { chave: 'at.restringir', secao: 'atendimentos', tipo: 'boolean', rotulo: 'Restringir acesso a prontuários', padrao: true, descricao: 'Atendimentos ficam restritos ao profissional que os realizou.' },
    { chave: 'at.cnes', secao: 'atendimentos', tipo: 'text', rotulo: 'Código CNES', padrao: '', descricao: 'Cadastro Nacional de Estabelecimentos de Saúde.' },
    { chave: 'at.orcamento-odontograma', secao: 'atendimentos', tipo: 'boolean', rotulo: 'Habilitar orçamentos no odontograma', padrao: true },

    /* ---------- Contatos ---------- */
    { chave: 'ct.obrigatorios', secao: 'contatos', tipo: 'composta', rotulo: 'Campos de cadastro obrigatório', padrao: null, padraoRotulo: 'Nome e telefone', editor: 'campos' },
    { chave: 'ct.personalizados', secao: 'contatos', tipo: 'composta', rotulo: 'Campos personalizados', padrao: null, padraoRotulo: 'Nenhum', editor: 'campos' },

    /* ---------- Impressão ---------- */
    {
        chave: 'imp.modelo', secao: 'impressao', tipo: 'composta', rotulo: 'Modelo de impressão', padrao: null, padraoRotulo: 'Padrão Fórmula MED', editor: 'impressao',
        descricao: 'Cabeçalho, rodapé, logotipo e margens dos documentos impressos.',
    },

    /* ---------- Comissões ---------- */
    { chave: 'com.atendimentos', secao: 'comissoes', tipo: 'boolean', rotulo: 'Comissionar atendimentos', padrao: true },
    { chave: 'com.vendas', secao: 'comissoes', tipo: 'boolean', rotulo: 'Comissionar vendas', padrao: true },
    { chave: 'com.disponibilidade', secao: 'comissoes', tipo: 'composta', rotulo: 'Forma de disponibilidade', padrao: null, padraoRotulo: 'Conforme o recebimento', editor: 'comissao', descricao: 'No ato da venda, na finalização da comanda ou conforme o recebimento.' },
    { chave: 'com.material', secao: 'comissoes', tipo: 'composta', rotulo: 'Material de atendimento', padrao: null, padraoRotulo: 'Custo da clínica', editor: 'comissao', descricao: 'Comportamento do custo de materiais no cálculo da comissão.' },
    { chave: 'com.taxas', secao: 'comissoes', tipo: 'composta', rotulo: 'Taxas de pagamentos', padrao: null, padraoRotulo: 'Clínica', editor: 'comissao', descricao: 'Clínica, profissional ou proporcional.' },
    { chave: 'com.custo-adicional', secao: 'comissoes', tipo: 'composta', rotulo: 'Custo adicional de serviços', padrao: null, padraoRotulo: 'Clínica', editor: 'comissao', descricao: 'Clínica, profissional ou proporcional.' },

    /* ---------- Orçamentos ---------- */
    { chave: 'orc.vencimento', secao: 'orcamentos', tipo: 'number', rotulo: 'Vencimento de orçamentos', padrao: 30, unidade: 'dias', descricao: 'Prazo de validade da proposta enviada ao paciente.' },
    { chave: 'orc.observacao', secao: 'orcamentos', tipo: 'composta', rotulo: 'Observação de orçamentos', padrao: null, padraoRotulo: 'Vazio', editor: 'texto', descricao: 'Texto fixo no rodapé de todo orçamento.' },

    /* ---------- Estoque ---------- */
    { chave: 'est.negativo', secao: 'estoque', tipo: 'boolean', rotulo: 'Permitir estoque negativo', padrao: false },
    { chave: 'est.lotes', secao: 'estoque', tipo: 'boolean', rotulo: 'Controlar lotes e validades', padrao: false },
    { chave: 'est.patrimonio', secao: 'estoque', tipo: 'boolean', rotulo: 'Controlar patrimônio', padrao: false },
    { chave: 'est.kit', secao: 'estoque', tipo: 'boolean', rotulo: 'Controlar Composição/Kit', padrao: false },
    { chave: 'est.expediente', secao: 'estoque', tipo: 'boolean', rotulo: 'Controlar Materiais de Expediente', padrao: false },

    /* ---------- Segurança ---------- */
    { chave: 'seg.mfa', secao: 'seguranca', tipo: 'boolean', rotulo: 'Exigir autenticação de dois fatores', padrao: false, descricao: 'Exige 2FA de todos os profissionais no próximo acesso.' },

    /* ---------- Vendas ---------- */
    {
        chave: 'vd.disponibilizacao', secao: 'vendas', tipo: 'select', rotulo: 'Forma de disponibilização de créditos, saldos e cashback', padrao: 'pagamento',
        opcoes: op(['pagamento', 'Pagamento'], ['venda', 'No ato da venda'], ['recebimento', 'Conforme o recebimento']),
    },
    { chave: 'vd.cashback', secao: 'vendas', tipo: 'boolean', rotulo: 'Gerar cashback em vendas', padrao: false },
    { chave: 'vd.cashback-produtos', secao: 'vendas', tipo: 'number', rotulo: 'Valor padrão de cashback para produtos', padrao: 0, unidade: '%' },
    { chave: 'vd.cashback-procedimentos', secao: 'vendas', tipo: 'number', rotulo: 'Valor padrão de cashback para procedimentos', padrao: 0, unidade: '%' },
    { chave: 'vd.cashback-credito', secao: 'vendas', tipo: 'number', rotulo: 'Valor padrão de cashback para venda de crédito (saldo)', padrao: 0, unidade: '%' },
    { chave: 'vd.validade-cashback', secao: 'vendas', tipo: 'number', rotulo: 'Validade do cashback', padrao: 0, unidade: 'minutos', descricao: 'Zero significa que o cashback nunca expira.' },

    /* ---------- Notificações ---------- */
    {
        chave: 'not.whatsapp', secao: 'notificacoes', tipo: 'select', rotulo: 'Envio de mensagens pelo WhatsApp', padrao: 'aplicativo',
        descricao: 'Por onde a mensagem sai quando a equipe dispara manualmente.',
        opcoes: op(['aplicativo', 'Aplicativo'], ['web', 'WhatsApp Web']),
    },
];

/** Métodos de pagamento do protótipo: rótulo das opções quando a clínica não tem cadastro. */
export const METODOS_DO_PROTOTIPO = METODOS;

export function preferenciaPorChave(chave: string): Preferencia | undefined {
    return PREFERENCIAS.find((p) => p.chave === chave);
}

/**
 * O valor em vigor: o salvo, ou o padrão do catálogo.
 *
 * Toda leitura de preferência pelo resto do app passa por aqui — assim a
 * pergunta "qual é o padrão" tem uma resposta só.
 */
export function valorEmVigor(chave: string, salvas: { chave: string; valor: ValorDePreferencia }[]): ValorDePreferencia | null {
    const salva = salvas.find((s) => s.chave === chave);
    if (salva) return salva.valor;
    return preferenciaPorChave(chave)?.padrao ?? null;
}

/** Mesmo valor? Objetos compostos comparados pelo conteúdo, não pela referência. */
export function mesmoValor(a: ValorDePreferencia | null | undefined, b: ValorDePreferencia | null | undefined): boolean {
    return JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
}
